// app/javascript/pages/Student/Profile/Show.tsx
import { useState, useRef } from 'react'
import type React from 'react'
import { router, Link } from '@inertiajs/react'
import { StudentShell } from '@/components/shared/StudentShell'
import { AvatarCropperModal } from '@/components/student/AvatarCropperModal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { ThemePicker } from '@/components/shared/ThemePicker'
import type { BadgeData } from '@/types'
import {
  User, BookOpen, Trophy, Coins, Bookmark, Flame, Edit3, Save, X,
  CheckCircle2, Sparkles, Camera, Percent, Shield, Link2, Unlink, KeyRound,
  AlertTriangle, Trash2
} from 'lucide-react'

interface ProfileData {
  id: number
  name: string
  email: string
  bio: string | null
  phone: string | null
  city: string | null
  avatar_color: string
  avatar_base64?: string | null
  avatar_url?: string | null
  preferred_language: string
  member_since: string
}

interface Props {
  profile: ProfileData
  enrolled_exams: Array<{ id: number; name: string; code: string }>
  all_exams: Array<{ id: number; name: string; code: string }>
  badges: BadgeData[]
  stats: {
    total_attempts: number
    avg_accuracy: number
    credit_balance: number
    bookmarks_count: number
    badges_count: number
    streak_days: number
  }
  connected_providers?: Array<{ provider: string; email: string; name?: string; connected_at: string }>
  has_password?: boolean
}

export default function ProfileShow({ profile, enrolled_exams, all_exams, badges: _badges, stats, connected_providers = [], has_password = false }: Props) {
  const [editing, setEditing] = useState(false)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)

  const [form, setForm] = useState({
    name: profile.name,
    bio: profile.bio || '',
    phone: profile.phone || '',
    city: profile.city || '',
    avatar_color: profile.avatar_color,
    avatar_base64: profile.avatar_base64 || '',
    preferred_language: profile.preferred_language
  })

  const [imgError, setImgError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const initials = form.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      if (event.target?.result) {
        setSelectedImageSrc(event.target.result as string)
        setCropperOpen(true)
      }
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handleCropComplete = (croppedBase64: string) => {
    setForm(f => ({ ...f, avatar_base64: croppedBase64 }))
    router.patch('/student/profile', { profile: { ...form, avatar_base64: croppedBase64 } })
  }

  const handleSave = () => {
    router.patch('/student/profile', { profile: form })
    setEditing(false)
  }

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteConsentChecked, setDeleteConsentChecked] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteAccount = () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE' || !deleteConsentChecked) return
    setIsDeleting(true)
    router.delete('/student/profile', {
      onFinish: () => {
        setIsDeleting(false)
        setDeleteModalOpen(false)
      }
    })
  }

  const handleEnrollToggle = (examId: number, enrolled: boolean) => {
    if (enrolled) {
      if (confirm('Are you sure you want to unenroll from this exam?')) {
        router.delete(`/student/exams/${examId}/unenroll`, { preserveScroll: true })
      }
    } else {
      router.post(`/student/exams/${examId}/enroll`, {}, { preserveScroll: true })
    }
  }

  const enrolledIds = enrolled_exams.map(e => e.id)

  const statCards = [
    { icon: Trophy, label: 'Tests Taken', value: stats.total_attempts, color: 'text-primary' },
    { icon: Percent, label: 'Accuracy', value: `${stats.avg_accuracy}%`, color: 'text-secondary' },
    { icon: Coins, label: 'Credits', value: stats.credit_balance, color: 'text-tertiary' },
    { icon: Bookmark, label: 'Bookmarks', value: stats.bookmarks_count, color: 'text-primary' },
    { icon: Trophy, label: 'Badges', value: stats.badges_count, color: 'text-secondary' },
    { icon: Flame, label: 'Streak', value: `${stats.streak_days}d`, color: 'text-tertiary' },
  ]

  return (
    <StudentShell>
      {/* Hidden File Input for Avatar Upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Avatar Cropper Dialog */}
      <AvatarCropperModal
        isOpen={cropperOpen}
        imageSrc={selectedImageSrc}
        onClose={() => setCropperOpen(false)}
        onCropComplete={handleCropComplete}
      />

      {/* Account Deletion & Consent Revocation Modal (DPDP Act 2023) */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setDeleteModalOpen(open)
            if (!open) {
              setDeleteConfirmText('')
              setDeleteConsentChecked(false)
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-md border-border bg-card shadow-glass-lg rounded-2xl p-6">
          <DialogHeader className="space-y-2 text-left">
            <div className="flex items-center gap-2 text-destructive">
              <div className="p-2 rounded-xl bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <DialogTitle className="text-base sm:text-lg font-bold text-foreground">
                  Delete Account
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Are you sure you want to delete your PrepKiya account?
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            {/* Warning callout */}
            <div className="p-3.5 rounded-xl bg-destructive/10 border border-destructive/20 space-y-2">
              <p className="font-semibold text-destructive flex items-center gap-1.5">
                <span>⚠️ Before you proceed, please note:</span>
              </p>
              <ul className="list-disc pl-4 space-y-1.5 text-muted-foreground leading-relaxed">
                <li><strong className="text-foreground">Personal Data:</strong> Your profile details, contact information, and connected logins will be permanently erased.</li>
                <li><strong className="text-foreground">Immediate Sign Out:</strong> You will be signed out immediately and won't be able to log back in.</li>
                <li><strong className="text-foreground">Test History & Credits:</strong> Your test attempts, analytics, bookmarks, badges, and any remaining test credits will be permanently lost and cannot be refunded.</li>
                <li><strong className="text-foreground">No Recovery:</strong> This action cannot be reversed or undone.</li>
              </ul>
            </div>

            {/* Acknowledgment Checkbox */}
            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="consent-checkbox"
                checked={deleteConsentChecked}
                onCheckedChange={(checked) => setDeleteConsentChecked(checked === true)}
                className="mt-0.5 border-border data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
              />
              <label
                htmlFor="consent-checkbox"
                className="text-xs text-muted-foreground leading-tight cursor-pointer select-none"
              >
                I understand that this action is <span className="font-bold text-foreground">permanent and irreversible</span>, and all my test progress and credits will be forfeited.
              </label>
            </div>

            {/* Type DELETE confirmation */}
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="delete-confirm-input" className="text-[11px] font-bold text-foreground">
                To confirm, type <span className="font-mono font-extrabold text-destructive">DELETE</span> below:
              </Label>
              <Input
                id="delete-confirm-input"
                type="text"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="font-mono text-sm border-border bg-background focus-visible:ring-destructive"
                disabled={isDeleting}
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2 flex flex-col-reverse sm:flex-row">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
              className="rounded-xl border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={deleteConfirmText.trim().toUpperCase() !== 'DELETE' || !deleteConsentChecked || isDeleting}
              onClick={handleDeleteAccount}
              className="rounded-xl gap-1.5 font-bold"
            >
              <Trash2 className="h-4 w-4" />
              <span>{isDeleting ? 'Deleting Account...' : 'Permanently Delete Account'}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── MOBILE VIEW ONLY (< lg) ─── */}
      <div className="block lg:hidden space-y-4 pb-6">
        {/* Hero Header */}
        <div className="space-y-1 pt-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest">
            <User className="h-3 w-3" />
            Student Profile
          </span>
          <h1 className="text-xl font-extrabold text-foreground tracking-tight">Profile & Preferences</h1>
          <p className="text-xs text-muted-foreground">Manage your identity, UI themes, and enrolled exams.</p>
        </div>

        {/* Quick Metrics Strip */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex flex-col items-center py-2.5 rounded-2xl border border-primary/20 bg-primary/5 font-bold">
            <span className="text-xl font-extrabold text-primary">{enrolled_exams.length}</span>
            <span className="text-[10px] font-semibold text-primary uppercase tracking-wider mt-0.5">Enrolled</span>
          </div>

          <div className="flex flex-col items-center py-2.5 rounded-2xl border border-secondary/20 bg-secondary/5 font-bold">
            <span className="text-xl font-extrabold text-secondary">{stats.avg_accuracy}%</span>
            <span className="text-[10px] font-semibold text-secondary uppercase tracking-wider mt-0.5">Accuracy</span>
          </div>

          <div className="flex flex-col items-center py-2.5 rounded-2xl border border-tertiary/20 bg-tertiary/5 font-bold">
            <span className="text-xl font-extrabold text-tertiary">{stats.credit_balance}</span>
            <span className="text-[10px] font-semibold text-tertiary uppercase tracking-wider mt-0.5">Credits</span>
          </div>
        </div>

        {/* Appearance & Theme Settings Card */}
        <div className="p-4 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5 min-w-0">
              <h2 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-secondary shrink-0" /> Appearance & Theme
              </h2>
              <p className="text-[11px] text-muted-foreground">Customize color theme & UI mode.</p>
            </div>
            <div className="shrink-0">
              <ThemePicker />
            </div>
          </div>
        </div>

        {/* Personal Information Card */}
        <div className="p-4 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-4">
          <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-3">
            <h2 className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-primary shrink-0" /> Personal Information
            </h2>
            {!editing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                className="rounded-xl font-bold text-xs h-8 px-2.5 border-primary/40 text-primary hover:bg-primary/10 shadow-2xs"
              >
                <Edit3 className="h-3 w-3 mr-1" /> Edit Profile
              </Button>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(false)}
                  className="rounded-xl font-bold text-xs h-8 w-8 p-0 border-border text-foreground hover:bg-muted/40"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className="rounded-xl font-bold text-xs h-8 px-2.5 bg-primary text-primary-fg shadow-2xs"
                >
                  <Save className="h-3 w-3 mr-1" /> Save
                </Button>
              </div>
            )}
          </div>

          {/* Avatar Section */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative group">
              <div className="w-20 h-20 rounded-2xl bg-primary text-primary-fg flex items-center justify-center text-xl font-extrabold shadow-2xs overflow-hidden border-2 border-primary/30">
                {(form.avatar_base64 || profile.avatar_url) && !imgError ? (
                  <img
                    src={form.avatar_base64 || profile.avatar_url || ''}
                    alt={form.name}
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={() => setImgError(true)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 p-1.5 rounded-xl bg-primary text-primary-fg shadow-md hover:scale-105 transition-all border-2 border-card"
                title="Upload profile photo"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground font-medium">Member since {profile.member_since}</p>
          </div>

          {/* Form Fields / Details */}
          {editing ? (
            <div className="space-y-3 pt-1">
              <div className="space-y-1">
                <Label className="text-xs font-bold">Full Name</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="h-9 rounded-xl text-xs bg-background border-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-bold">City</Label>
                  <Input
                    value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    className="h-9 rounded-xl text-xs bg-background border-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-bold">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="h-9 rounded-xl text-xs bg-background border-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold">Language</Label>
                <Select
                  value={form.preferred_language}
                  onValueChange={(val) => setForm(f => ({ ...f, preferred_language: val }))}
                >
                  <SelectTrigger className="w-full h-9 rounded-xl border border-input bg-background px-3 py-1.5 text-xs text-foreground font-medium focus:ring-primary">
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                    <SelectGroup>
                      <SelectItem value="en" className="text-xs font-medium cursor-pointer">English</SelectItem>
                      <SelectItem value="hi" className="text-xs font-medium cursor-pointer">हिंदी</SelectItem>
                      <SelectItem value="mr" className="text-xs font-medium cursor-pointer">मराठी</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold">Bio</Label>
                <Textarea
                  value={form.bio}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  rows={2}
                  placeholder="Tell us about your study goals..."
                  className="rounded-xl text-xs bg-background border-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2.5 pt-1">
              <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Name</span>
                <span className="text-xs font-bold text-foreground truncate max-w-[200px]">{profile.name}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Email</span>
                <span className="text-xs font-semibold text-foreground truncate max-w-[200px]">{profile.email}</span>
              </div>
              {profile.city && (
                <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                  <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">City</span>
                  <span className="text-xs font-semibold text-foreground">{profile.city}</span>
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                  <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Phone</span>
                  <span className="text-xs font-semibold text-foreground">{profile.phone}</span>
                </div>
              )}
              {profile.bio && (
                <div className="pt-1">
                  <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider block mb-0.5">Bio</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{profile.bio}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security & Connected Accounts Card */}
        <div className="p-4 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-3">
          <h2 className="text-xs font-bold text-foreground flex items-center gap-1.5 border-b border-border/40 pb-2.5">
            <Shield className="h-3.5 w-3.5 text-primary shrink-0" /> Security & Logins
          </h2>

          {/* Google OAuth row */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-muted/20">
            <div className="flex items-center gap-2.5 min-w-0">
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="min-w-0">
                <p className="text-xs font-bold text-foreground">Google</p>
                {(() => {
                  const googleIdentity = connected_providers.find(p => p.provider === 'google_oauth2')
                  return googleIdentity ? (
                    <p className="text-[10px] text-muted-foreground truncate">{googleIdentity.email || 'Connected'}</p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground">Not connected</p>
                  )
                })()}
              </div>
            </div>

            {(() => {
              const googleIdentity = connected_providers.find(p => p.provider === 'google_oauth2')
              if (googleIdentity) {
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (connected_providers.length === 1 && !has_password) {
                        alert('Please set a password before disconnecting your only sign-in method.')
                        return
                      }
                      if (confirm('Disconnect Google account?')) {
                        router.delete('/student/identities/google_oauth2')
                      }
                    }}
                    className="text-[11px] font-bold rounded-xl h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
                  >
                    <Unlink className="h-3 w-3 mr-1" /> Disconnect
                  </Button>
                )
              }

              return (
                <form method="post" action="/users/auth/google_oauth2">
                  <input
                    type="hidden"
                    name="authenticity_token"
                    value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''}
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    size="sm"
                    className="text-[11px] font-bold rounded-xl h-8 px-2.5 border-primary/40 text-primary hover:bg-primary/10 shrink-0 cursor-pointer"
                  >
                    <Link2 className="h-3 w-3 mr-1" /> Connect
                  </Button>
                </form>
              )
            })()}
          </div>

          {/* Password Management row */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-muted/20">
            <div className="flex items-center gap-2.5 min-w-0">
              <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-bold text-foreground">Password</p>
                <p className="text-[10px] text-muted-foreground">
                  {has_password ? 'Configured' : 'Not set'}
                </p>
              </div>
            </div>
            <a
              href="/users/edit"
              className="text-[11px] font-bold rounded-xl h-8 px-2.5 inline-flex items-center gap-1 border border-border bg-card text-foreground hover:bg-muted/40 transition-colors shrink-0"
            >
              <span>{has_password ? 'Change' : 'Set'}</span>
            </a>
          </div>
        </div>

        {/* 6 Quick Stats Cards */}
        <div className="grid grid-cols-3 gap-2">
          {statCards.map(s => (
            <div key={s.label} className="text-center p-2.5 rounded-2xl border border-border/70 bg-card shadow-2xs">
              <p className="text-base font-extrabold text-foreground">{s.value}</p>
              <p className="text-[10px] text-muted-foreground font-bold truncate mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Enrolled Exams Card */}
        <div className="p-4 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2.5">
            <h2 className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" /> My Enrolled Exams
            </h2>
            <Link href="/student/exams" className="text-xs font-semibold text-primary hover:underline">
              Manage
            </Link>
          </div>

          <div className="space-y-2">
            {all_exams.filter(exam => enrolledIds.includes(exam.id)).length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">
                You are not enrolled in any entrance exams yet.
              </p>
            ) : (
              all_exams
                .filter(exam => enrolledIds.includes(exam.id))
                .map(exam => (
                  <div
                    key={exam.id}
                    className="flex items-center justify-between p-3 rounded-2xl border border-primary/20 bg-primary/5 gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground block">{exam.code}</span>
                        <span className="text-xs font-extrabold text-foreground truncate block">
                          {exam.name}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[10px] font-bold rounded-xl h-7 px-2 border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0"
                      onClick={() => handleEnrollToggle(exam.id, true)}
                    >
                      Unenroll
                    </Button>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Mobile Danger Zone: Account Deletion */}
        <div className="p-4 rounded-2xl border border-destructive/30 bg-card shadow-2xs space-y-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="text-[9px] font-bold border-destructive/30 bg-destructive/10 text-destructive rounded-md px-1.5 py-0"
              >
                Danger Zone
              </Badge>
              <span className="text-xs font-bold text-foreground">
                Delete Account
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Permanently delete your account, personal data, and test history. This action cannot be undone.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDeleteModalOpen(true)}
            className="w-full text-xs font-bold rounded-xl h-8 border-destructive/40 text-destructive hover:bg-destructive/10 cursor-pointer"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete Account
          </Button>
        </div>
      </div>

      {/* ─── DESKTOP VIEW ONLY (>= lg) ─── */}
      <div className="hidden lg:block space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <User className="h-3 w-3 text-primary" />
                  <span>Student Profile</span>
                </span>
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                Profile & Account Preferences
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Manage your student identity, customize your UI themes, and review your enrolled target exams.
              </p>
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-3 gap-2.5 sm:gap-3 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col justify-center p-3 sm:p-3.5 rounded-xl bg-primary/10 border border-primary/20 backdrop-blur-xs">
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Enrolled</span>
                <span className="text-lg sm:text-xl font-bold text-primary mt-0.5">{enrolled_exams.length}</span>
              </div>

              <div className="flex flex-col justify-center p-3 sm:p-3.5 rounded-xl bg-secondary/10 border border-secondary/20 backdrop-blur-xs">
                <span className="text-[10px] font-bold text-secondary uppercase tracking-wider">Accuracy</span>
                <span className="text-lg sm:text-xl font-bold text-foreground mt-0.5">{stats.avg_accuracy}%</span>
              </div>

              <div className="flex flex-col justify-center p-3 sm:p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/20 backdrop-blur-xs">
                <div className="flex items-center gap-1">
                  <Coins className="h-3 w-3 text-tertiary" />
                  <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">Credits</span>
                </div>
                <span className="text-lg sm:text-xl font-bold text-foreground mt-0.5">{stats.credit_balance}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance & Theme Settings Card */}
        <Card className="rounded-2xl border border-border/80 bg-card shadow-glass-sm p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <h3 className="text-sm sm:text-base font-extrabold text-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-secondary shrink-0" /> Appearance & Theme Settings
              </h3>
              <p className="text-xs text-muted-foreground">
                Customize your color theme and UI mode.
              </p>
            </div>
            <div className="w-full sm:w-auto pt-1 sm:pt-0">
              <ThemePicker />
            </div>
          </div>
        </Card>

        {/* Profile Card */}
        <Card className="rounded-2xl border border-border/80 bg-card shadow-glass-sm p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 border-b border-border/40 pb-3">
            <h3 className="text-sm sm:text-base font-extrabold text-foreground flex items-center gap-2">
              <User className="h-4 w-4 text-primary shrink-0" /> Personal Information
            </h3>
            {!editing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                className="rounded-xl font-bold text-xs h-8 sm:h-9 border-primary/40 text-primary hover:bg-primary/10 shadow-xs cursor-pointer"
              >
                <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Profile
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(false)}
                  className="rounded-xl font-bold text-xs h-8 sm:h-9 border-border text-foreground hover:bg-muted/40 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className="rounded-xl font-bold text-xs h-8 sm:h-9 bg-primary text-primary-fg shadow-xs cursor-pointer"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
            {/* Interactive Photo Avatar */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="relative group">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-primary text-primary-fg flex items-center justify-center text-xl sm:text-2xl font-extrabold shadow-glass-sm overflow-hidden border-2 border-primary/40">
                  {(form.avatar_base64 || profile.avatar_url) && !imgError ? (
                    <img
                      src={form.avatar_base64 || profile.avatar_url || ''}
                      alt={form.name}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={() => setImgError(true)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 p-1.5 rounded-xl bg-primary text-primary-fg shadow-md hover:scale-105 transition-all border-2 border-card cursor-pointer"
                  title="Upload profile photo"
                >
                  <Camera className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground font-medium">Member since {profile.member_since}</p>
            </div>

            {/* Form Fields / View Mode */}
            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {editing ? (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold">Full Name</Label>
                    <Input
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="h-9 rounded-xl text-xs bg-background border-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold">City</Label>
                    <Input
                      value={form.city}
                      onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      className="h-9 rounded-xl text-xs bg-background border-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold">Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="h-9 rounded-xl text-xs bg-background border-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold">Language</Label>
                    <Select
                      value={form.preferred_language}
                      onValueChange={(val) => setForm(f => ({ ...f, preferred_language: val }))}
                    >
                      <SelectTrigger className="w-full h-9 rounded-xl border border-input bg-background px-3 py-1.5 text-xs text-foreground font-medium focus:ring-primary">
                        <SelectValue placeholder="Select Language" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                        <SelectGroup>
                          <SelectItem value="en" className="text-xs font-medium cursor-pointer">English</SelectItem>
                          <SelectItem value="hi" className="text-xs font-medium cursor-pointer">हिंदी</SelectItem>
                          <SelectItem value="mr" className="text-xs font-medium cursor-pointer">मराठी</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 space-y-1">
                    <Label className="text-xs font-bold">Bio</Label>
                    <Textarea
                      value={form.bio}
                      onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                      rows={2}
                      placeholder="Tell us about your study goals..."
                      className="rounded-xl text-xs bg-background border-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-0.5">
                    <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Name</p>
                    <p className="font-extrabold text-sm sm:text-base text-foreground">{profile.name}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Email</p>
                    <p className="font-semibold text-xs sm:text-sm text-foreground truncate">{profile.email}</p>
                  </div>
                  {profile.city && (
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">City</p>
                      <p className="font-semibold text-xs sm:text-sm text-foreground">{profile.city}</p>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Phone</p>
                      <p className="font-semibold text-xs sm:text-sm text-foreground">{profile.phone}</p>
                    </div>
                  )}
                  {profile.bio && (
                    <div className="sm:col-span-2 space-y-0.5 pt-1">
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Bio</p>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{profile.bio}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Security & Connected Accounts Card */}
        <Card className="rounded-2xl border border-border/80 bg-card shadow-glass-sm p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 border-b border-border/40 pb-3">
            <Shield className="h-4 w-4 text-primary shrink-0" />
            <h3 className="text-sm sm:text-base font-extrabold text-foreground">
              Security & Connected Accounts
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Google OAuth row */}
            <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl border border-border bg-background">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground">Google</p>
                  {(() => {
                    const googleIdentity = connected_providers.find(p => p.provider === 'google_oauth2')
                    return googleIdentity ? (
                      <p className="text-[11px] text-muted-foreground truncate">
                        {googleIdentity.email || 'Connected'} · {googleIdentity.connected_at}
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Not connected</p>
                    )
                  })()}
                </div>
              </div>

              {(() => {
                const googleIdentity = connected_providers.find(p => p.provider === 'google_oauth2')
                if (googleIdentity) {
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (connected_providers.length === 1 && !has_password) {
                          alert('Please set a password before disconnecting your only sign-in method.')
                          return
                        }
                        if (confirm('Disconnect Google account?')) {
                          router.delete('/student/identities/google_oauth2')
                        }
                      }}
                      className="text-[11px] font-bold rounded-xl h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
                    >
                      <Unlink className="h-3 w-3 mr-1" /> Disconnect
                    </Button>
                  )
                }

                return (
                  <form method="post" action="/users/auth/google_oauth2">
                    <input
                      type="hidden"
                      name="authenticity_token"
                      value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''}
                    />
                    <Button
                      type="submit"
                      variant="outline"
                      size="sm"
                      className="text-[11px] font-bold rounded-xl h-8 px-2.5 border-primary/40 text-primary hover:bg-primary/10 shrink-0 cursor-pointer"
                    >
                      <Link2 className="h-3 w-3 mr-1" /> Connect
                    </Button>
                  </form>
                )
              })()}
            </div>

            {/* Password Management row */}
            <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl border border-border bg-background">
              <div className="flex items-center gap-3 min-w-0">
                <KeyRound className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground">Password</p>
                  <p className="text-[11px] text-muted-foreground">
                    {has_password ? 'Password is configured' : 'Google sign-in only (no password set)'}
                  </p>
                </div>
              </div>
              <a
                href="/users/edit"
                className="text-[11px] font-bold rounded-xl h-8 px-3 inline-flex items-center gap-1.5 border border-border bg-background text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
              >
                <KeyRound className="h-3 w-3 text-muted-foreground" />
                <span>{has_password ? 'Change Password' : 'Set Password'}</span>
              </a>
            </div>
          </div>
        </Card>

        {/* 6 Quick Stats Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          {statCards.map(s => (
            <Card key={s.label} className="text-center p-2.5 sm:p-3 rounded-2xl border border-border bg-card shadow-glass-xs">
              <p className="text-base sm:text-xl font-extrabold text-foreground">{s.value}</p>
              <p className="text-[10px] sm:text-[11px] text-muted-foreground font-bold truncate mt-0.5">{s.label}</p>
            </Card>
          ))}
        </div>

        {/* Enrolled Exams */}
        <Card className="rounded-2xl border border-border bg-card shadow-glass-sm p-4 sm:p-5">
          <CardHeader className="p-0 pb-3 border-b border-border/40 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-sm sm:text-base font-extrabold flex items-center gap-2 text-foreground">
              <BookOpen className="h-4 w-4 text-primary shrink-0" />
              <span>My Enrolled Exams</span>
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex text-xs font-bold rounded-xl h-8 px-3 border-border text-foreground hover:bg-muted/40 shadow-xs shrink-0 cursor-pointer"
              asChild
            >
              <Link href="/student/exams">Manage All Exams</Link>
            </Button>
          </CardHeader>

          <CardContent className="p-0 pt-3.5 space-y-3">
            <div className="grid gap-2.5">
              {all_exams.filter(exam => enrolledIds.includes(exam.id)).length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  You are not enrolled in any entrance exams yet.
                </p>
              ) : (
                all_exams
                  .filter(exam => enrolledIds.includes(exam.id))
                  .map(exam => (
                    <div
                      key={exam.id}
                      className="flex items-center justify-between p-3 sm:p-3.5 rounded-2xl border border-primary/30 bg-primary/5 shadow-glass-xs gap-2"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge
                              variant="outline"
                              className="font-mono text-[10px] font-extrabold border-primary/30 text-primary bg-primary/10 rounded-md px-1.5 py-0"
                            >
                              {exam.code}
                            </Badge>
                            <span className="text-xs sm:text-sm font-extrabold text-foreground truncate">
                              {exam.name}
                            </span>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[11px] font-bold rounded-xl h-7 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
                        onClick={() => handleEnrollToggle(exam.id, true)}
                      >
                        Unenroll
                      </Button>
                    </div>
                  ))
              )}
            </div>

            {/* Bottom button for mobile */}
            <div className="pt-1 sm:hidden">
              <Button
                variant="outline"
                className="w-full text-xs font-bold rounded-xl h-9 border-border text-foreground hover:bg-muted/40 shadow-xs cursor-pointer"
                asChild
              >
                <Link href="/student/exams">Manage All Exams</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Desktop Danger Zone: Account Deletion */}
        <Card className="rounded-2xl border border-destructive/30 bg-card p-5 shadow-glass-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-[10px] font-bold border-destructive/30 bg-destructive/10 text-destructive rounded-md px-2 py-0.5"
                >
                  Danger Zone
                </Badge>
                <span className="text-sm font-bold text-foreground">
                  Delete Account
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Permanently delete your account, personal data, and test history. This action cannot be undone.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteModalOpen(true)}
              className="text-xs font-bold rounded-xl h-9 px-3.5 border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete Account
            </Button>
          </div>
        </Card>
      </div>
    </StudentShell>
  )
}
